/** @format */
import { ChangeDetectionStrategy, Component, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { StateService } from '@uirouter/core';
import { Attainment, AttainmentType } from 'common-typescript/types';
import moment from 'moment';
import { map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'sis-common';
import {
    AttainmentEntityService,
    isAssessmentItemAttainment,
    isAttached,
    isCourseUnitAttainment,
    isCourseUnitLikeAttainment,
    isCustomCourseUnitAttainment,
    toChildAttainmentIds,
} from 'sis-components';
import { AttainmentStudentService } from '../../../common/service/attainment-student.service';
import { getAttainmentDetailsModalOpener } from '../../../profile/attainment/attainment-details/attainment-details-modal/attainment-details-modal.component';

@Component({
    selector: 'app-recent-attainments',
    templateUrl: './recent-attainments.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class RecentAttainmentsComponent implements OnInit {
    readonly attainmentService = inject(AttainmentEntityService);
    readonly attainmentStudentService = inject(AttainmentStudentService);
    private readonly authService = inject(AuthService);
    private readonly openAttainmentDetailsModal = getAttainmentDetailsModalOpener();

    readonly profileAttainmentsHref = inject(StateService).href('student.logged-in.profile.attainments');
    readonly searchFailed = signal(false);

    recentAttainments$: Observable<Attainment[]>;
    allAttainments$: Observable<Attainment[]>;
    private isDetailsModalOpen = false;

    readonly isAssessmentItemAttainment = isAssessmentItemAttainment;
    readonly isCourseUnitAttainment = isCourseUnitAttainment;
    readonly isCustomCourseUnitAttainment = isCustomCourseUnitAttainment;
    readonly isCourseUnitLikeAttainment = isCourseUnitLikeAttainment;
    readonly isAttached = isAttached;
    readonly toChildAttainmentIds = toChildAttainmentIds;

    ngOnInit(): void {
        this.allAttainments$ = this.attainmentStudentService.getMyUnfilteredAttainments().pipe(shareReplay(1));
        this.recentAttainments$ = this.findRecentAttainments(this.allAttainments$);
    }

    onAttainmentClick(clickedAttainment: Attainment): void {
        if (!this.isDetailsModalOpen) {
            this.isDetailsModalOpen = true;
            this.allAttainments$
                .pipe(
                    take(1),
                    map(allStudentAttainments =>
                        this.openAttainmentDetailsModal({
                            attainment: clickedAttainment,
                            secondaryAttainments: this.attainmentService
                                .getSecondaryAttainments(clickedAttainment, allStudentAttainments)
                                .filter(({ misregistration }) => !misregistration),
                            hasValidParentAttainment: this.isAttached(clickedAttainment, allStudentAttainments),
                            isAboutToExpire: this.attainmentService.isAboutToExpire(clickedAttainment),
                            gradeRaiseEnabled: false,
                            gradeRaiseInfoEnabled: true,
                            gradeRaiseAttempt: null,
                            isAttachedToPrimaryAttainment: !!allStudentAttainments.find(
                                att =>
                                    this.toChildAttainmentIds(att).includes(clickedAttainment.id) && !att.misregistration && !!att.primary,
                            ),
                        }),
                    ),
                    switchMap(modalRef => modalRef.hidden),
                )
                .subscribe(() => (this.isDetailsModalOpen = false));
        }
    }

    private findRecentAttainments(allAttainments$: Observable<Attainment[]>): Observable<Attainment[]> {
        return allAttainments$.pipe(
            map(attainments => {
                const filteredAttainments = this.filterResults(attainments);
                const sortedAttainments = this.sortResults(filteredAttainments);
                return sortedAttainments.slice(0, 5);
            }),
            catchError(() => {
                this.searchFailed.set(true);
                return of([]);
            }),
            shareReplay(1),
        );
    }

    /**
     * Show only attainments with type: COURSE_UNIT_ATTAINMENT, CUSTOM_COURSE_UNIT_ATTAINMENT, ASSESSMENT_ITEM_ATTAINMENT.
     * AssessmentItemAttainments are only shown if those are not yet attached to courseUnitAttainment.
     **/
    private filterResults(attainments: Attainment[]): Attainment[] {
        if (attainments.length === 0) {
            return attainments;
        }

        const cuAttainments = attainments.filter(att => this.isCourseUnitLikeAttainment(att));
        const assItemAttainments = attainments.filter(att => this.isAssessmentItemAttainment(att));
        const assItemAttainmentsAttachedRemoved = assItemAttainments.filter(att => !this.isAttached(att, cuAttainments));

        return [...cuAttainments, ...assItemAttainmentsAttachedRemoved].filter(attainment => {
            if (attainment.misregistration) {
                return false;
            }
            if (!attainment.primary && attainment.state !== 'FAILED') {
                return false;
            }
            return !(attainment.expiryDate && moment(attainment.expiryDate).isSameOrBefore(moment(), 'days'));
        });
    }

    /*
     * SortOrder is:
     * - attainmentDate
     *   if attainments have same date:
     *   - type
     *       if attainments have same date & type, e.g.: 'AssessmentItemAttainment':
     *       - metadata.createdOn
     * */
    private sortResults(attainments: Attainment[]): Attainment[] {
        if (attainments.length === 0) {
            return attainments;
        }

        const typeOrder: { [key: string]: number } = {
            [AttainmentType.COURSE_UNIT_ATTAINMENT]: 1,
            [AttainmentType.CUSTOM_COURSE_UNIT_ATTAINMENT]: 2,
            [AttainmentType.ASSESSMENT_ITEM_ATTAINMENT]: 3,
        };

        return attainments.sort(
            (a, b) =>
                b.attainmentDate.localeCompare(a.attainmentDate) ||
                typeOrder[a.type] - typeOrder[b.type] ||
                b.metadata.createdOn.localeCompare(a.metadata.createdOn),
        );
    }
}
